import type { PublicPricingSport, PublicPricingTier } from '../../types/pricing';
import { formatPrice } from '../signup/signupFormat';
import { PricingCta } from './PricingCta';

const SPORT_LABEL: Record<PublicPricingSport, string> = {
  baseball: 'Baseball',
  softball: 'Softball',
};

/**
 * The tier's yearly prices: one row per sport it sells, each labelled with its sport. An API from
 * before prices differed by sport sends no `sportPrices`, so the single legacy price shows alone.
 */
const TierPrices = ({ tier, currency }: { tier: PublicPricingTier; currency: string }) => {
  if (tier.sportPrices.length === 0) {
    return (
      <p className="pricing-card__price" data-testid={`pricing-price-${tier.key}`}>
        <span className="pricing-card__amount">{formatPrice(tier.annualPriceCents, currency)}</span>{' '}
        <span className="pricing-card__per">/ year</span>
      </p>
    );
  }

  return (
    <ul className="pricing-card__prices" aria-label={`${tier.name} yearly prices`} data-testid={`pricing-price-${tier.key}`}>
      {tier.sportPrices.map((price) => (
        <li
          key={price.sport}
          className="pricing-card__sport-price"
          data-testid={`pricing-price-${tier.key}-${price.sport}`}
        >
          <span className="pricing-card__sport">{SPORT_LABEL[price.sport]}</span>
          <span className="pricing-card__price">
            <span className="pricing-card__amount">{formatPrice(price.annualPriceCents, currency)}</span>{' '}
            <span className="pricing-card__per">/ year</span>
          </span>
        </li>
      ))}
    </ul>
  );
};

/** The marketing site's check mark (its `.tick` lists). Decoration: the text carries the meaning. */
export const Tick = () => (
  <svg
    className="pricing-card__tick"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    aria-hidden="true"
    focusable="false"
  >
    <path d="m4 13 5 5L20 6" />
  </svg>
);

/**
 * One tier: who it is for, its yearly prices, and the way in. Rendered inside the tier list's `li`.
 * `headingLevel` is 4 when the cards sit under a sport heading (the page with baseball plans).
 */
export const PricingTierCard = ({
  tier,
  currency,
  signupAvailable,
  trialDays,
  headingLevel = 3,
}: {
  tier: PublicPricingTier;
  currency: string;
  signupAvailable: boolean;
  trialDays: number | null;
  headingLevel?: 3 | 4;
}) => {
  const Heading = headingLevel === 4 ? 'h4' : 'h3';
  return (
    <article className="pricing-card" aria-labelledby={`pricing-tier-${tier.key}`} data-testid={`pricing-tier-${tier.key}`}>
      <Heading className="pricing-card__name" id={`pricing-tier-${tier.key}`}>
        {tier.name}
      </Heading>
      {tier.description && <p className="pricing-card__for">{tier.description}</p>}
      <TierPrices tier={tier} currency={currency} />
      <ul className="pricing-card__facts">
        <li>
          <Tick />
          <span>One program, one sport</span>
        </li>
        {trialDays !== null && (
          <li>
            <Tick />
            <span>{`Free for the first ${trialDays} days`}</span>
          </li>
        )}
        <li>
          <Tick />
          <span>Billed once a year</span>
        </li>
      </ul>
      <div className="pricing-card__action">
        <PricingCta
          signupAvailable={signupAvailable}
          tierName={tier.name}
          block
          testId={`pricing-cta-${tier.key}`}
        />
      </div>
    </article>
  );
};
